// Kbd — the keyboard-shortcut hint primitive, pure.
//
// Renders a shortcut like `⌘K`, `⇧⌘P`, or `Esc` as key caps, for the calm
// affordance the material toolbar / command surface leans on (`⌘K` in the
// frame, .claude/research/design/02). It is the simplest primitive here: a small inline
// element that styles machine-shaped input in the mono face — the same
// trust-signal register as Mono (foundations §2.5), machine data visually
// split from authored prose.
//
// A combo is modelled as an ARRAY of key strings (`keys`), never a magic
// delimited string ("⌘+K") the component would have to parse — each entry is
// its own cap, so the caller keeps the structure and the component keeps the
// look. A single key is just a one-element array.
//
// PLATFORM REGISTER (ui-design-synthesis §2): a key entry is either an
// abstract modifier TOKEN — "mod", "ctrl", "shift", "alt", lowercase on
// purpose so it can never be mistaken for a literal cap — or a literal label
// rendered verbatim ("K", "Esc", "↵", and legacy hardcoded glyphs like "⌘").
// Tokens render in the platform's own convention (Nielsen #4):
//   mac            → concatenated glyph caps  ⌘ ⌃ ⇧ ⌥   (no joiner)
//   windows/linux  → text caps Ctrl/Shift/Alt joined by "+"
// The platform arrives as an OPTIONAL prop; when omitted it defaults to
// "mac", the pre-platform visual status quo, so existing literal call sites
// render identically. Hosts feed the real platform via the app-side boundary
// reader `resolveKbdPlatform()` (src/app/kbdPlatform.ts — bridge-preferring,
// impure, deliberately NOT imported here: this component stays pure).
// An explicit `joiner` prop always wins over the platform default.
//
// Semantic markup: an outer <kbd> wraps the whole combo, each cap is a nested
// <kbd> — the MDN-sanctioned shape for "a sequence of keystrokes". The joiner
// is aria-hidden (decoration between caps). No colour carries meaning here, so
// SC 1.4.1 is not in play. The primitive authors no user-facing string of its
// own (key labels are caller-owned or platform-conventional glyphs/OS words
// that are not translated), so it needs no i18n macro.
//
// Pure/props-driven: `keys` is the data, `platform` picks the register,
// `joiner` overrides the look; every other element attribute (title, id,
// aria-*) passes through. No state, no effects, no boundary reads.

import { Fragment, type HTMLAttributes, type ReactNode } from "react";
import "./Kbd.css";

// The OS family whose shortcut convention the caps follow. Structurally
// compatible with the app layer's DesktopPlatform (same string values, no
// import edge — components never import from src/app).
export type KbdPlatform = "mac" | "windows" | "linux";

// Abstract modifier tokens. "mod" is the platform's primary command modifier
// (⌘ on mac, Ctrl on windows/linux); "ctrl" is the literal Control key on
// every platform (⌃ on mac). Lowercase distinguishes tokens from literal caps.
type KbdToken = "mod" | "ctrl" | "shift" | "alt";

const isKbdToken = (key: string): key is KbdToken =>
    key === "mod" || key === "ctrl" || key === "shift" || key === "alt";

// The two token registers. Values are OS-conventional symbols/words, not
// authored prose — they are never translated.
const macCaps: Record<KbdToken, string> = {
    mod: "⌘",
    ctrl: "⌃",
    shift: "⇧",
    alt: "⌥",
};
const textCaps: Record<KbdToken, string> = {
    mod: "Ctrl",
    ctrl: "Ctrl",
    shift: "Shift",
    alt: "Alt",
};

// Pure register lookup: what one key entry renders as on a platform. Tokens
// map through the platform's register; literals pass through verbatim.
// Exported so callers (menus, tooltips) can reuse the exact same mapping.
const kbdKeyLabel = (key: string, platform: KbdPlatform): string =>
    isKbdToken(key) ? (platform === "mac" ? macCaps : textCaps)[key] : key;

export type KbdProps = HTMLAttributes<HTMLElement> & {
    // The keys of the shortcut, in press order. One entry renders one cap; a
    // single-key hint is a one-element array. Entries are abstract tokens
    // ("mod", "shift", "alt", "ctrl") or literal labels ("K", "Esc", "↵").
    readonly keys: readonly string[];
    // Which platform register the tokens (and the default joiner) follow.
    // Defaults to "mac" — hosts feed the detected platform (see header).
    readonly platform?: KbdPlatform;
    // Presentational glyph placed between caps, overriding the platform
    // default (mac: none, the `⇧⌘P` register; windows/linux: "+").
    readonly joiner?: ReactNode;
};

export const Kbd = ({
    keys,
    platform = "mac",
    joiner,
    className,
    ...rest
}: KbdProps) => {
    const sep = joiner ?? (platform === "mac" ? null : "+");
    return (
        <kbd
            className={`bilda-kbd${className ? ` ${className}` : ""}`}
            {...rest}
        >
            {keys.map((key, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: the caps are a fixed presentational sequence with no identity of their own (two identical glyphs are legitimate) and never reorder
                <Fragment key={index}>
                    {index > 0 && sep != null ? (
                        <span className="bilda-kbd__joiner" aria-hidden="true">
                            {sep}
                        </span>
                    ) : null}
                    <kbd className="bilda-kbd__key">
                        {kbdKeyLabel(key, platform)}
                    </kbd>
                </Fragment>
            ))}
        </kbd>
    );
};
